import { useEffect, type RefObject } from "react";

/**
 * Routes horizontal wheel and trackpad input over the grid through AG Grid's
 * own horizontal scrollbar.
 *
 * Left alone, the browser scrolls the grid body on its compositor thread, while
 * AG Grid moves the header rows and any aligned grids (RatesGrid, SummaryGrid)
 * from a JavaScript scroll handler a frame or more later, so they visibly trail
 * the body. Scrolling the grid's scrollbar instead makes AG Grid move the body,
 * the header, and the aligned grids together in the same step.
 */
export const useSyncedHorizontalWheel = (
  containerRef: RefObject<HTMLElement | null>,
): void => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (event: WheelEvent): void => {
      // Vertical-dominant input scrolls the page.
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      const scrollbar = container.querySelector<HTMLElement>(
        ".ag-body-horizontal-scroll-viewport",
      );
      if (!scrollbar || scrollbar.scrollWidth <= scrollbar.clientWidth) return;

      event.preventDefault();
      scrollbar.scrollLeft += event.deltaX;
    };

    // Non-passive so preventDefault can stop the native body scroll.
    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [containerRef]);
};
